import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { Role, Theme } from '@shared/protocol';
import { api, ApiError, setCsrfToken, setUnauthorizedHandler } from './api';
import { applyTheme } from './theme';

export interface User {
  id: number;
  email: string;
  role: Role;
  theme: Theme;
  /** Most streams the account may add (User role only); null means no limit. */
  streamLimit: number | null;
}

interface AuthState {
  user: User | null;
  loading: boolean;
  needsSetup: boolean;
  login: (email: string, password: string) => Promise<void>;
  completeSetup: (token: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setTheme: (theme: Theme) => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

interface SessionResponse {
  user: User;
  csrfToken: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [needsSetup, setNeedsSetup] = useState(false);

  const accept = useCallback((s: SessionResponse) => {
    setCsrfToken(s.csrfToken);
    setUser(s.user);
    applyTheme(s.user.theme);
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setCsrfToken(null);
      setUser(null);
    });
    (async () => {
      try {
        accept(await api<SessionResponse>('GET', '/api/auth/me'));
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) {
          const s = await api<{ needsSetup: boolean }>('GET', '/api/setup/status').catch(() => ({ needsSetup: false }));
          setNeedsSetup(s.needsSetup);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [accept]);

  const value = useMemo<AuthState>(
    () => ({
      user,
      loading,
      needsSetup,
      login: async (email, password) => accept(await api<SessionResponse>('POST', '/api/auth/login', { email, password })),
      completeSetup: async (token, email, password) => {
        accept(await api<SessionResponse>('POST', '/api/setup', { token, email, password }));
        setNeedsSetup(false);
      },
      logout: async () => {
        await api('POST', '/api/auth/logout').catch(() => {});
        setCsrfToken(null);
        setUser(null);
      },
      setTheme: async (theme) => {
        applyTheme(theme);
        const r = await api<{ user: User }>('PATCH', '/api/me', { theme });
        setUser(r.user);
      },
    }),
    [user, loading, needsSetup, accept],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
